import { Link } from "react-router-dom";
import { ArrowRight, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { safeLink, safeImage } from "@/components/home/pageBlocks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const BG = {
  white: "bg-white text-black",
  grey: "bg-[#F5F5F5] text-black",
  black: "bg-black text-white",
  yellow: "bg-[#EFDF00] text-black",
};

const CtaButton = ({ label, link, dark, testid }) => {
  const target = safeLink(link);
  if (!label || !target) return null;
  const btn = (
    <Button className={`${dark ? "bg-[#EFDF00] text-black hover:bg-[#D9C900]" : "bg-black text-white hover:bg-[#333]"} font-bold px-6 py-5`} data-testid={testid}>
      {label}
      <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
  );
  return target.internal ? <Link to={target.href}>{btn}</Link> : <a href={target.href} target="_blank" rel="noopener noreferrer">{btn}</a>;
};

const TextBlock = ({ p, id }) => (
  <section className={`py-16 ${BG[p.background] || BG.white}`} data-testid={`custom-text-${id}`}>
    <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${p.align === "center" ? "text-center" : ""}`}>
      {p.title && <h2 className="text-3xl md:text-4xl font-bold normal-case tracking-tight mb-4">{p.title}</h2>}
      <div className={`w-16 h-1 bg-[#EFDF00] mb-6 ${p.align === "center" ? "mx-auto" : ""} ${p.background === "yellow" ? "bg-black" : ""}`} />
      <p className={`whitespace-pre-line leading-relaxed ${p.background === "black" ? "text-[#D9D9D6]" : "text-[#555]"}`}>{p.text}</p>
    </div>
  </section>
);

const ImageTextBlock = ({ p, id }) => {
  const img = safeImage(p.image_url, BACKEND_URL);
  return (
    <section className="py-20 bg-white" data-testid={`custom-image-text-${id}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className={p.image_side === "left" ? "lg:order-2" : ""}>
          {p.title && <h2 className="text-3xl md:text-4xl font-bold text-black normal-case tracking-tight mb-4">{p.title}</h2>}
          <div className="w-16 h-1 bg-[#EFDF00] mb-6" />
          <p className="text-[#555] whitespace-pre-line leading-relaxed mb-8">{p.text}</p>
          <CtaButton label={p.button_label} link={p.button_link} testid={`custom-btn-${id}`} />
        </div>
        <div className={p.image_side === "left" ? "lg:order-1" : ""}>
          {img ? <img src={img} alt={p.title || ""} className="w-full max-h-[420px] object-cover" />
            : <div className="w-full h-72 bg-[#F2F2F2] flex items-center justify-center text-[#888B8D] text-sm">Noch kein Bild gewählt</div>}
        </div>
      </div>
    </section>
  );
};

const BannerBlock = ({ p, id }) => (
  <section className="bg-black" data-testid={`custom-banner-${id}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-white font-bold text-lg">{p.text}</p>
      <CtaButton label={p.button_label} link={p.button_link} dark testid={`custom-btn-${id}`} />
    </div>
  </section>
);

export const CustomBlock = ({ block }) => {
  const p = block.props || {};
  if (block.type === "text") return <TextBlock p={p} id={block.id} />;
  if (block.type === "image_text") return <ImageTextBlock p={p} id={block.id} />;
  if (block.type === "banner") return <BannerBlock p={p} id={block.id} />;
  return null;
};

export const BlockFrame = ({ block, preview, selected, label, children }) => {
  if (!preview) return children;
  const select = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.parent.postMessage({ type: "at-editor-select", id: block.id }, window.location.origin);
  };
  return (
    <div className={`relative group ${selected ? "outline outline-4 outline-[#EFDF00] -outline-offset-4" : "hover:outline hover:outline-2 hover:outline-dashed hover:outline-[#EFDF00] hover:-outline-offset-2"}`}
      id={`block-${block.id}`} data-testid={`preview-block-${block.id}`}>
      <button type="button" onClick={select}
        className={`absolute top-3 right-3 z-30 items-center gap-1 bg-[#EFDF00] text-black text-xs font-bold px-3 py-1.5 shadow-lg ${selected ? "flex" : "hidden group-hover:flex"}`}
        data-testid={`preview-edit-${block.id}`}>
        <Pencil className="w-3 h-3" /> {label} bearbeiten
      </button>
      {children}
    </div>
  );
};
